import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import TextAlign from "@tiptap/extension-text-align";
import Color from "@tiptap/extension-color";
import { TextStyle } from "@tiptap/extension-text-style";
import { Box } from "@mui/material";

// Add this component above MoreWaysModal
const TipTapEditor = ({ value, onChange, disabled }) => {
  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Link.configure({ openOnClick: false }),
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      TextStyle,
      Color,
    ],
    content: value,
    editable: !disabled,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
  });

  if (!editor) return null;

  const btnStyle = (isActive) => ({
    padding: "4px 8px",
    margin: "2px",
    cursor: disabled ? "not-allowed" : "pointer",
    backgroundColor: isActive ? "#683C8C" : "#fff",
    color: isActive ? "#fff" : "#333",
    border: "1px solid #ccc",
    borderRadius: "4px",
    fontSize: "13px",
  });

  return (
    <Box
      color={"initial"}
      sx={{
        border: "1px solid #ccc",
        borderRadius: "4px",
        backgroundColor: disabled ? "#f5f5f5" : "#fff",
      }}
    >
      {/* Toolbar */}
      {!disabled && (
        <Box
          sx={{
            borderBottom: "1px solid #ccc",
            padding: "6px 8px",
            display: "flex",
            flexWrap: "wrap",
            gap: "2px",
          }}
        >
          {/* Heading */}
          <select
            onChange={(e) => {
              const val = e.target.value;
              if (val === "0") {
                editor.chain().focus().setParagraph().run();
              } else {
                editor
                  .chain()
                  .focus()
                  .toggleHeading({ level: parseInt(val) })
                  .run();
              }
            }}
            style={{
              padding: "4px",
              border: "1px solid #ccc",
              borderRadius: "4px",
              fontSize: "13px",
              cursor: "pointer",
            }}
          >
            <option value="0">Normal</option>
            <option value="1">H1</option>
            <option value="2">H2</option>
            <option value="3">H3</option>
          </select>

          <button
            type="button"
            onClick={() => editor.chain().focus().toggleBold().run()}
            style={btnStyle(editor.isActive("bold"))}
          >
            <b>B</b>
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleItalic().run()}
            style={btnStyle(editor.isActive("italic"))}
          >
            <i>I</i>
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleUnderline().run()}
            style={btnStyle(editor.isActive("underline"))}
          >
            <u>U</u>
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleStrike().run()}
            style={btnStyle(editor.isActive("strike"))}
          >
            <s>S</s>
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            style={btnStyle(editor.isActive("bulletList"))}
          >
            • List
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            style={btnStyle(editor.isActive("orderedList"))}
          >
            1. List
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().setTextAlign("left").run()}
            style={btnStyle(editor.isActive({ textAlign: "left" }))}
          >
            Left
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().setTextAlign("center").run()}
            style={btnStyle(editor.isActive({ textAlign: "center" }))}
          >
            Center
          </button>
          <button
            type="button"
            onClick={() => editor.chain().focus().setTextAlign("right").run()}
            style={btnStyle(editor.isActive({ textAlign: "right" }))}
          >
            Right
          </button>
          <button
            type="button"
            onClick={() => {
              const url = window.prompt("Enter URL");
              if (url) editor.chain().focus().setLink({ href: url }).run();
            }}
            style={btnStyle(editor.isActive("link"))}
          >
            Link
          </button>
          <button
            type="button"
            onClick={() =>
              editor.chain().focus().unsetAllMarks().clearNodes().run()
            }
            style={btnStyle(false)}
          >
            Clear
          </button>
          <input
            type="color"
            title="Text Color"
            value={editor.getAttributes("textStyle").color || "#000000"}
            onChange={(e) =>
              editor.chain().focus().setColor(e.target.value).run()
            }
            style={{
              width: "34px",
              height: "30px",
              padding: "2px",
              border: "1px solid #ccc",
              borderRadius: "4px",
              cursor: "pointer",
              background: "#fff",
            }}
          />
          <button
            type="button"
            onClick={() => editor.chain().focus().unsetColor().run()}
            style={btnStyle(false)}
          >
            Reset Color
          </button>
        </Box>
      )}

      {/* Editor Area */}
      <EditorContent
        editor={editor}
        style={{
          padding: "10px",
          height: "100%",
          fontSize: "14px",
        }}
      />

      {/* Bullet fix styles */}
      <style>{`
  .tiptap ul { list-style-type: disc !important; padding-left: 1.5em !important; }
  .tiptap ol { list-style-type: decimal !important; padding-left: 1.5em !important; }
  .tiptap p { margin: 0.3em 0; }
  .tiptap:focus { outline: none !important; }
  .tiptap { outline: none !important; }
  .ProseMirror { outline: none !important; }
  .ProseMirror:focus { outline: none !important; }
`}</style>
    </Box>
  );
};

export default TipTapEditor;
